/* eslint-disable @typescript-eslint/naming-convention */
import axiosInstanceApiGateway from 'config/apiServiceApiGateway'

export type HomeTabComponentSegmentationType = {
  id?: number
  type_id: number
  segment_type_id: number
  segment_type_name?: string
}

export type HomeTabComponentDataType = {
  homepage_tabbing_component_id: number
  homepage_tabbing_id: number
  homepage_tabbing_component_type_id: number
  component_name: string
  position: number
  /** Layout blocks (`1x2`, `1x3`, …). Empty for Banner Regular. */
  layout: string
  /** IDs of the linked content — banner ids for Banner Regular. */
  reference_ids?: number[]
  /** Tiles JSON — Category Specific only, omitted for every other component. */
  additional_info?: string
  targeting_type_id: number
  targeted_user_ids?: number[]
  start_date_time?: string
  end_date_time?: string
  is_recurring_schedule: boolean
  is_active: boolean
  location_ids?: number[]
  targeted_segmentations?: HomeTabComponentSegmentationType[]
}

/**
 * The API has sent `additional_info` both as a JSON string and as the parsed
 * value, so reading it stays untyped and is normalised on the way in.
 */
export type GetHomeTabComponentResponseType = {
  data: Omit<HomeTabComponentDataType, 'additional_info'> & { additional_info?: unknown }
}

export const getHomeTabComponent = (tabId: number, componentId: number) =>
  axiosInstanceApiGateway.Get<GetHomeTabComponentResponseType, true>({
    url: `/browse/internal/v1/homepage-tabbings/${tabId}/components/${componentId}`,
  })

export type CreateHomeTabComponentPayloadType = Omit<
  HomeTabComponentDataType,
  'homepage_tabbing_component_id'
>

export type CreateHomeTabComponentResponseType = GetHomeTabComponentResponseType

export const createHomeTabComponent = (payload: CreateHomeTabComponentPayloadType) =>
  axiosInstanceApiGateway.Post<CreateHomeTabComponentResponseType, true>({
    url: `/browse/internal/v1/homepage-tabbings/${payload.homepage_tabbing_id}/components`,
    data: payload,
  })

export type DeleteHomeTabComponentPayloadType = {
  homepage_tabbing_id: number
  homepage_tabbing_component_id: number
}

export type DeleteHomeTabComponentResponseType = {
  error?: {
    status: boolean
    message: string
    code: number
  }
}

/** Both ids ride in the path; the gateway's Delete helper sends no body. */
export const deleteHomeTabComponent = ({
  homepage_tabbing_id,
  homepage_tabbing_component_id,
}: DeleteHomeTabComponentPayloadType) =>
  axiosInstanceApiGateway.Delete<DeleteHomeTabComponentResponseType, true>({
    url: `/browse/internal/v1/homepage-tabbings/${homepage_tabbing_id}/components/${homepage_tabbing_component_id}`,
  })

export type UpdateHomeTabComponentPayloadType = HomeTabComponentDataType

export type UpdateHomeTabComponentResponseType = GetHomeTabComponentResponseType

export const updateHomeTabComponent = (payload: UpdateHomeTabComponentPayloadType) =>
  axiosInstanceApiGateway.Put<UpdateHomeTabComponentResponseType, true>({
    url: `/browse/internal/v1/homepage-tabbings/${payload.homepage_tabbing_id}/components/${payload.homepage_tabbing_component_id}`,
    data: payload,
  })

export type HomeTabComponentSortItemType = {
  id: number
  /** 1-based position; must be unique within the request. */
  position: number
}

export type SortHomeTabComponentsPayloadType = {
  homepage_tabbing_id: number
  components: HomeTabComponentSortItemType[]
}

/** Reorders every component of a tab in one atomic call. */
export type SortHomeTabComponentsResponseType = {
  message: string
  status: number
}

export const sortHomeTabComponents = ({
  homepage_tabbing_id,
  components,
}: SortHomeTabComponentsPayloadType) =>
  axiosInstanceApiGateway.Put<SortHomeTabComponentsResponseType, true>({
    url: `/browse/internal/v1/homepage-tabbings/${homepage_tabbing_id}/components/sort`,
    data: { components },
  })
